import { Injectable } from '@angular/core';
import { GamepadConnectionService } from './gamepad-connection.service';

export const GAMEPAD_LOCK_ATTRIBUTE = 'gamepadlock';

export type GamepadLock = 'host' | 'widget';

/**
 * `navigator.getGamepads()` is a broadcast read with no exclusive claim, so the native gamepad
 * pipeline and a gamepad-capable custom widget will happily drive the same robot at once. This
 * arbiter keeps one gamepad session per page: whoever arms last owns it, and the loser is stopped.
 *
 * Widget -> host is the `gamepad-armed` / `gamepad-disarmed` CustomEvents; host -> widget is the
 * `gamepadlock` attribute. A widget that ignores the attribute is simply never locked, so the
 * protocol degrades to today's behaviour rather than breaking.
 */
@Injectable({
  providedIn: 'root',
})
export class GamepadArbiterService {
  private elements = new Set<HTMLElement>();
  private owner: 'host' | HTMLElement | null = null;

  constructor(private gamepadConnectionService: GamepadConnectionService) {
    this.gamepadConnectionService.gamepadEnabled$.subscribe((enabled) => {
      if (enabled) {
        this.owner = 'host';
        this.applyLocks();
      } else if (this.owner === 'host') {
        this.owner = null;
        this.applyLocks();
      }
    });
  }

  /** Called as each custom widget element is mounted, so one mounted mid-session starts locked. */
  public register(element: HTMLElement): void {
    this.elements.add(element);
    this.applyLock(element);
  }

  public unregister(element: HTMLElement): void {
    this.elements.delete(element);
    element.removeAttribute(GAMEPAD_LOCK_ATTRIBUTE);

    if (this.owner === element) {
      this.owner = null;
      this.applyLocks();
    }
  }

  public widgetArmed(element: HTMLElement): void {
    // Ownership is claimed before the toggle below, so the synchronous `gamepadEnabled$` emission
    // it triggers sees an owner that is no longer 'host' and leaves this widget's claim intact.
    this.owner = element;

    if (this.gamepadConnectionService.gamepadEnabled$.value) {
      // Disable as the user rather than automatically: an automatic disable leaves the native
      // service eligible to resume itself on the next window focus, which would preempt the widget
      // that just armed. Toggling emits the native pipeline's own zeroed safety frame.
      this.gamepadConnectionService.toggleGamepad(false, true);
    }

    this.applyLocks();
  }

  public widgetDisarmed(element: HTMLElement): void {
    if (this.owner !== element) return;

    this.owner = null;
    this.applyLocks();
  }

  private lockFor(element: HTMLElement): GamepadLock | null {
    if (this.owner === null || this.owner === element) return null;
    if (this.owner === 'host') return 'host';
    return 'widget';
  }

  private applyLocks(): void {
    this.elements.forEach((element) => this.applyLock(element));
  }

  private applyLock(element: HTMLElement): void {
    const lock = this.lockFor(element);

    if (lock === null) {
      element.removeAttribute(GAMEPAD_LOCK_ATTRIBUTE);
    } else if (element.getAttribute(GAMEPAD_LOCK_ATTRIBUTE) !== lock) {
      element.setAttribute(GAMEPAD_LOCK_ATTRIBUTE, lock);
    }
  }
}
